/**
 * Shared utility functions for chart components
 */

import { csvParse } from 'd3';

/**
 * Splits a comma-separated string and trims each value
 * @param {string | null} str - The string to split
 * @returns {Array<string>} Array of trimmed strings
 */
export function splitAndTrim(str) {
  return str ? str.split(',').map((s) => s.trim()) : [];
}

/**
 * Parse a value from CSV, handling percentages and comma-separated numbers
 * @param {string} value - The value to parse
 * @returns {number} Parsed numeric value
 */
export function parseValue(value) {
  // Handle percentage values - keep as decimal number (92.10% becomes 92.10)
  if (value.includes('%')) {
    return parseFloat(value.replace('%', ''));
  }

  // Remove dollar signs, commas, and other currency symbols
  const cleaned = value.replace(/[$,]/g, '');
  return parseFloat(cleaned);
}

/**
 * Format Y-axis tick value based on format type
 * @param {number} value - The value to format
 * @param {string} format - Format type: 'percentage', 'currency', or 'number'
 * @returns {string} Formatted value
 */
export function formatYAxis(value, format) {
  if (format === 'percentage') {
    return `${value.toFixed(0)}%`;
  }

  if (format === 'currency') {
    return `$${value.toLocaleString()}`;
  }

  return value.toLocaleString();
}

/**
 * Format tooltip value based on format type
 * @param {number} value - The value to format
 * @param {string} format - Format type: 'percentage', 'currency', or 'number'
 * @returns {string} Formatted value
 */
export function formatTooltip(value, format) {
  if (format === 'percentage') {
    return `${value.toFixed(2)}%`;
  }

  if (format === 'currency') {
    return `$${value.toLocaleString()}`;
  }

  return value.toLocaleString();
}

/**
 * Makes a chart responsive by re-rendering on window resize
 * @param {HTMLElement} container - The chart container element
 * @param {Function} renderFunction - The function to call to re-render the chart
 * @returns {Function} Cleanup function to remove the resize listener
 */
export function makeChartResponsive(container, renderFunction) {
  let resizeTimeout;

  const handleResize = () => {
    clearTimeout(resizeTimeout);
    resizeTimeout = setTimeout(() => {
      renderFunction();
    }, 150);
  };

  window.addEventListener('resize', handleResize);

  // Return cleanup function
  return () => {
    clearTimeout(resizeTimeout);
    window.removeEventListener('resize', handleResize);
  };
}

/**
 * Fetches and parses CSV data, then renders chart with responsive support
 * @param {HTMLElement} chartElement - The chart inner element
 * @param {string} file - URL to the CSV file
 * @param {string} wrapperClass - CSS class of the wrapper element
 * @param {Function} renderFunction - Function to call with parsed data
 * @param {Function} [onError] - Optional error handler
 * @param {boolean} [enableResponsive=true] - Whether to enable responsive resizing (disable for D3Plus charts)
 */
export function fetchChartData(
  chartElement,
  file,
  wrapperClass,
  renderFunction,
  onError,
  enableResponsive = true,
) {
  let data = null;

  const render = () => {
    if (!data) {
      return;
    }
    renderFunction(data);
  };

  fetch(file)
    .then((response) => response.text())
    .then((csvText) => {
      data = csvParse(csvText);
      render();

      // Only make responsive if enabled (raw D3 charts only)
      if (enableResponsive) {
        const wrapper = chartElement.closest(wrapperClass);
        if (wrapper instanceof HTMLElement) {
          makeChartResponsive(wrapper, render);
        }
      }
    })
    .catch((error) => {
      if (onError) {
        onError(error);
      } else {
        chartElement.innerHTML = `<p>Error loading chart data: ${error.message}</p>`;
      }
    });
}

/**
 * Calculates legend item widths
 * @param {Object} svg - D3 SVG selection
 * @param {Array} legendItems - Array of legend item objects with {label, color, type}
 * @returns {Array<number>} Array of item widths
 */
export function calculateLegendWidths(svg, legendItems) {
  const tempLegend = svg.append('g').attr('class', 'temp-legend');
  const legendWidths = [];

  for (const item of legendItems) {
    const tempItem = tempLegend.append('g');
    const tempText = tempItem
      .append('text')
      .style('font-size', '11px')
      .style(
        'font-family',
        '"Roboto", "Helvetica Neue", "HelveticaNeue", "Helvetica", "Arial", sans-serif',
      )
      .text(item.label);
    const bbox = tempText.node()?.getBBox();
    if (!bbox) {
      legendWidths.push(30);
      continue;
    }
    const itemWidth = bbox.width + 30; // 18 for icon + 12 for padding
    legendWidths.push(itemWidth);
  }
  tempLegend.remove();

  return legendWidths;
}

/**
 * Calculates legend row layout for wrapping
 * @param {Array<number>} legendWidths - Array of legend item widths
 * @param {number} maxWidth - Maximum width for a row
 * @returns {Array<Array<number>>} Array of rows, each containing item indices
 */
export function calculateLegendRows(legendWidths, maxWidth) {
  const rows = [];
  let currentRow = [];
  let currentRowWidth = 0;

  for (let i = 0; i < legendWidths.length; i++) {
    const itemWidth = legendWidths[i];

    if (currentRowWidth + itemWidth > maxWidth && currentRow.length > 0) {
      // Start a new row
      rows.push(currentRow);
      currentRow = [i];
      currentRowWidth = itemWidth;
    } else {
      currentRow.push(i);
      currentRowWidth += itemWidth;
    }
  }
  if (currentRow.length > 0) {
    rows.push(currentRow);
  }

  return rows;
}

/**
 * Sets up bidirectional hover highlighting between chart elements and legend
 * @param {Object} svg - D3 SVG selection
 * @param {Array} legendItems - Array of legend item objects with {label, element}
 * @param {Array} chartElements - Array of chart element objects with {series, element}
 */
export function setupHoverHighlight(svg, legendItems, chartElements) {
  /**
   * Highlights a specific series
   * @param {string} seriesName - The name of the series to highlight
   */
  function highlightSeries(seriesName) {
    svg.selectAll('.bar').style('opacity', 0.3);
    svg.selectAll('path').style('opacity', 0.3);
    svg.selectAll('.legend-item').style('opacity', 0.3);

    svg.selectAll(`[data-series="${seriesName}"]`).style('opacity', 1);
  }

  /**
   * Resets all highlights
   */
  function resetHighlight() {
    svg.selectAll('.bar').style('opacity', 1);
    svg.selectAll('path').style('opacity', 1);
    svg.selectAll('.legend-item').style('opacity', 1);
  }

  // Add hover effects for legend items
  for (const item of legendItems) {
    item.element
      .on('mouseenter', () => highlightSeries(item.label))
      .on('mouseleave', resetHighlight);
  }

  // Add hover effects for chart elements
  for (const item of chartElements) {
    item.element
      .on('mouseenter', () => highlightSeries(item.series))
      .on('mouseleave', resetHighlight);
  }
}
